import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Receipt, 
  Filter,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Download,
  Check,
  Send,
  Loader2
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDistanceToNow } from 'date-fns';

const InvoiceCard = ({ invoice, clientName, onClick, t, isSelected, onSelect }) => {
  const statusConfig = {
    unpaid: { icon: AlertCircle, color: 'bg-red-100 text-red-600', label: t('unpaid') },
    partial: { icon: Clock, color: 'bg-yellow-100 text-yellow-600', label: t('partial') },
    paid: { icon: CheckCircle, color: 'bg-green-100 text-green-600', label: t('paid') }
  };
  
  const status = statusConfig[invoice.status] || statusConfig.unpaid;
  const StatusIcon = status.icon;
  const balance = invoice.amount_due - (invoice.amount_paid || 0);
  
  return (
    <div
      className={`card-default w-full text-left group relative ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
      data-testid={`invoice-card-${invoice.id}`}
    >
      {/* Selection Checkbox */}
      <div 
        className="absolute top-3 left-3 z-10"
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.(invoice.id);
        }}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onSelect?.(invoice.id)}
          className="data-[state=checked]:bg-blue-600"
        />
      </div>
      
      <button onClick={onClick} className="w-full text-left">
        <div className="flex items-start justify-between mb-3 pl-8">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${status.color}`}>
              <StatusIcon className="w-5 h-5" />
            </div>
            <div>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${status.color}`}>
                {status.label}
              </span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
        
        <h3 className="font-heading text-lg font-semibold mb-1 group-hover:text-primary transition-colors">
          Invoice #{invoice.id.slice(0, 8).toUpperCase()}
        </h3>
        <p className="text-muted-foreground">{clientName}</p>
        
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div>
            <span className="text-sm text-muted-foreground">
              {formatDistanceToNow(new Date(invoice.created_at), { addSuffix: true })}
            </span>
            {invoice.due_date && (
              <p className="text-xs text-muted-foreground">
                {t('due_date')}: {new Date(invoice.due_date).toLocaleDateString()}
              </p>
            )}
          </div>
          <div className="text-right">
            <span className="font-heading text-xl font-bold">
              ${invoice.amount_due?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            {invoice.amount_paid > 0 && invoice.status !== 'paid' && (
              <p className="text-sm text-green-600">
                ${invoice.amount_paid?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('paid').toLowerCase()}
              </p>
            )}
          </div>
        </div>
      </button>
    </div>
  );
};

const InvoicesPage = () => {
  const { organization, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, [organization?.id]);

  const loadInvoices = async () => {
    if (!organization?.id) return;
    
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        apiHelpers.getInvoices(organization.id),
        apiHelpers.getClients(organization.id)
      ]);
      
      setInvoices(invoicesRes.data);
      
      // Create client lookup map
      const clientMap = {};
      clientsRes.data.forEach(client => {
        clientMap[client.id] = client.name;
      });
      setClients(clientMap);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      toast.error(t('failed_to_load'));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'all') return true;
    return inv.status === filter;
  });

  const filterOptions = [
    { value: 'all', label: t('all') },
    { value: 'unpaid', label: t('unpaid') },
    { value: 'partial', label: t('partial') },
    { value: 'paid', label: t('paid') }
  ];

  // Calculate totals
  const totalOutstanding = invoices
    .filter(inv => inv.status !== 'paid')
    .reduce((sum, inv) => sum + (inv.amount_due - (inv.amount_paid || 0)), 0);

  const handleExportCSV = async () => {
    try {
      const response = await apiHelpers.exportInvoices(organization.id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoices-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoices exported to CSV');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export invoices');
    }
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectedIds.length === filteredInvoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredInvoices.map(inv => inv.id));
    }
  };

  const handleSelectOne = (invoiceId) => {
    setSelectedIds(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleBulkMarkPaid = async () => {
    if (selectedIds.length === 0) return;
    
    setIsBulkProcessing(true);
    let successCount = 0;
    
    try {
      for (const invoiceId of selectedIds) {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        if (invoice && invoice.status !== 'paid') {
          await apiHelpers.updateInvoice(organization.id, invoiceId, {
            status: 'paid',
            amount_paid: invoice.amount_due,
            paid_at: new Date().toISOString()
          }, user?.id);
          successCount++;
        }
      }
      
      toast.success(`${successCount} invoice${successCount > 1 ? 's' : ''} marked as paid`);
      setSelectedIds([]);
      loadInvoices();
    } catch (error) {
      console.error('Bulk update failed:', error);
      toast.error('Failed to update some invoices');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkSendReminder = async () => {
    if (selectedIds.length === 0) return;
    
    setIsBulkProcessing(true);
    let successCount = 0;
    
    try {
      for (const invoiceId of selectedIds) {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        if (invoice && invoice.status !== 'paid') {
          await apiHelpers.sendInvoice(organization.id, invoiceId, user?.id, user?.name);
          successCount++;
        }
      }
      
      toast.success(`Reminder sent for ${successCount} invoice${successCount > 1 ? 's' : ''}`);
      setSelectedIds([]);
    } catch (error) {
      console.error('Bulk send failed:', error);
      toast.error('Failed to send some reminders');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            {t('invoices_title')}
          </h1>
          <p className="text-muted-foreground text-lg mt-1">
            {invoices.length} {t('total_invoices')}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            data-testid="export-invoices-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          
          {totalOutstanding > 0 && (
            <div className="card-default p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('outstanding')}</p>
                <p className="font-heading text-2xl font-bold text-red-600">
                  ${totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Checkbox
            checked={selectedIds.length === filteredInvoices.length}
            onCheckedChange={handleSelectAll}
            className="data-[state=checked]:bg-blue-600"
          />
          <span className="font-medium text-blue-900">
            {selectedIds.length} selected
          </span>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkSendReminder}
            disabled={isBulkProcessing}
            className="border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            {isBulkProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Reminders
          </Button>
          <Button
            size="sm"
            onClick={handleBulkMarkPaid}
            disabled={isBulkProcessing}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {isBulkProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Mark as Paid
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds([])}
          >
            Cancel
          </Button>
        </div>
      )}
      
      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {filterOptions.map((option) => (
          <Button
            key={option.value}
            variant={filter === option.value ? "default" : "outline"}
            onClick={() => setFilter(option.value)}
            className={`h-10 px-4 rounded-full ${
              filter === option.value ? 'bg-primary text-white' : ''
            }`}
            data-testid={`filter-${option.value}`}
          >
            {option.label}
            {option.value !== 'all' && (
              <span className="ml-2 opacity-70">
                ({invoices.filter(i => i.status === option.value).length})
              </span>
            )}
          </Button>
        ))}
      </div>
      
      {/* Invoices Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredInvoices.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInvoices.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              clientName={clients[invoice.client_id] || t('customers').slice(0, -1)}
              onClick={() => navigate(`/invoices/${invoice.id}`)}
              t={t}
              isSelected={selectedIds.includes(invoice.id)}
              onSelect={handleSelectOne}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 card-default">
          <Receipt className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="font-heading text-xl font-semibold mb-2">
            {t('no_results')}
          </h3>
          <p className="text-muted-foreground mb-6">
            {filter !== 'all' 
              ? t('try_different_search') 
              : t('try_different_search')}
          </p>
          {filter === 'all' && (
            <Button
              onClick={() => navigate('/estimates')}
              className="btn-primary"
            >
              {t('view')} {t('estimates')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
